var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button } from "@animech/ui";
import { useTheme } from "@emotion/react";
import * as Styled from "./graph.style";
import { AirConsumptionGraph as AirConsumptionGraph } from "./air-consumption-graph";
import { RobotMotionGraph as RobotMotionGraph } from "./robot-motion-graph";
import { VacuumPumpGraph } from "./vacuum-pump-graph";
import { ChartBackground } from "./common/custom-chart-background";
import { LiftingForcesGraph } from "ui/components/panels/graphs/lifting-forces-graph";
import { actions, selectors, useAppDispatch, useAppSelector } from "store";
import { useBlockingElementEffect } from "hooks/playcanvas/blocking-element-effect";
import { InfoButton } from "ui/components/elements/info-button";
import { NodeList } from "ui/components/panels/graphs/common/node-list";
import { useGraphDataContext } from "providers/graph-panel";
import { CalculateButton } from "ui/components/panels/topbar/calculate-button";
import { LoadingOverlay } from "ui/components/elements/loading-overlay";
import { PlayLine } from "ui/components/panels/graphs/common/play-simulation/play-line";
import { useTimeDataMinMaxDomain } from "hooks/graph-panel/graph";
import { CupAnimation2D } from "ui/components/panels/dashboard/boxes/application-speed/cup-animation-2d";
import { GraphAnimationInfoBox } from "ui/components/panels/graphs/graph-animation-info-box";
import { useChartElementPositions } from "ui/components/elements/chart/use-chart-element-positions";
import { getGraphPlayLinePercent } from "ui/components/panels/graphs/play-line-position";
import { IconButton } from "ui/components/elements/icons/icon-button";
import { OpenInWindowIcon } from "ui/components/elements/icons/open-in-window";
import { DockIcon } from "ui/components/elements/icons/dock";
import { PopoutWindow, useFitToWindowScale, } from "ui/components/elements/popout-window/popout-window";
import { autoOpenedGraphPanelVisibleMs, graphHeight, } from "ui/components/panels/graphs/constants";
var CloseButton = function () {
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    return (_jsx(Button, { appearance: "text", color: "inverted", icon: "close", rounded: true, size: "default", rootAttributes: { "aria-label": t("graph_panel.close") }, onClick: function () { return dispatch(actions.toggleGraphPanel(false)); } }));
};
var DataInvalid = function () {
    var t = useTranslation().t;
    return (_jsx(Styled.DataInvalid, { children: _jsxs(Styled.DataInvalidContent, { children: [_jsx(Styled.DataInvalidClose, { children: _jsx(CloseButton, {}) }), _jsx("p", { children: t("graph_panel.data_invalid_sentence_1") }), _jsx("p", { children: t("graph_panel.data_invalid_sentence_2") }), _jsx(CalculateButton, {})] }) }));
};
var PopOutButton = function () {
    var t = useTranslation().t;
    var _a = useGraphDataContext(), graphPanelWindow = _a.graphPanelWindow, dashboardWindow = _a.dashboardWindow, popOut = _a.popOut, dock = _a.dock;
    // The panel moves with the popped-out dashboard (see DashboardPopOutButton).
    if (dashboardWindow)
        return null;
    var label = t(graphPanelWindow ? "graph_panel.dock" : "graph_panel.pop_out");
    return (_jsx(IconButton, { appearance: "text", color: "inverted", onClick: graphPanelWindow ? dock : function () { return popOut("graph-panel"); }, tooltip: label, rootAttributes: { "aria-label": label }, children: graphPanelWindow ? (_jsx(DockIcon, { size: 18 })) : (_jsx(OpenInWindowIcon, { size: 18 })) }));
};
/** Blocks the 3D scene while mounted, for the docked panel that covers it. */
var CanvasBlocker = function () {
    useBlockingElementEffect();
    return null;
};
var GraphPanelContent = function (_a) {
    var shouldRenderGraphData = _a.shouldRenderGraphData;
    var t = useTranslation().t;
    var isDataLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var isDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var multipleBoxesEnabled = useAppSelector(selectors.selectMultipleBoxesEnabled);
    var showLoadingPanel = !isDataValid && isDataLoading;
    var showOutdatedDataWarning = shouldRenderGraphData && !isDataValid && !showLoadingPanel;
    return (_jsxs(_Fragment, { children: [showOutdatedDataWarning && _jsx(DataInvalid, {}), showLoadingPanel && _jsx(LoadingOverlay, {}), _jsx(Styled.TopBar, { children: _jsxs(Styled.ButtonsContainer, { children: [_jsx(InfoButton, { title: t("dashboard.application_cycle_data"), description: {
                                long: t(multipleBoxesEnabled
                                    ? "descriptions.dashboard.application_cycle_data_multi_box"
                                    : "descriptions.dashboard.application_cycle_data"),
                            } }), _jsx(PopOutButton, {}), _jsx(CloseButton, {})] }) }), shouldRenderGraphData && _jsx(GraphDataContainer, {})] }));
};
var PopoutGraphPanel = function (_a) {
    var targetWindow = _a.targetWindow;
    var theme = useTheme();
    var scale = useFitToWindowScale(targetWindow, Styled.graphPanelWidth(theme), graphHeight);
    return (_jsx(PopoutWindow, { targetWindow: targetWindow, children: _jsx(Styled.PopoutGraphPanel, { scale: scale, children: _jsx(GraphPanelContent, { shouldRenderGraphData: true }) }) }));
};
export var GraphPanel = function () {
    var dispatch = useAppDispatch();
    var _a = useGraphDataContext(), printModeActive = _a.printModeActive, graphPanelWindow = _a.graphPanelWindow, dashboardWindow = _a.dashboardWindow;
    var isGraphPanelOpen = useAppSelector(selectors.selectIsGraphPanelOpen);
    var isAutoOpened = useAppSelector(selectors.selectIsGraphPanelAutoOpened);
    var _b = __read(useState(false), 2), shouldRenderGraphData = _b[0], setShouldRenderGraphData = _b[1];
    // Opened by the first calculation, the panel only presents itself: it fades
    // out again after a few seconds unless the user starts using it, and the
    // pulsing dashboard buttons take over inviting them to open it.
    useEffect(function () {
        if (!isAutoOpened || !isGraphPanelOpen)
            return;
        var timer = window.setTimeout(function () { return dispatch(actions.hideAutoOpenedGraphPanel()); }, autoOpenedGraphPanelVisibleMs);
        return function () { return window.clearTimeout(timer); };
    }, [dispatch, isAutoOpened, isGraphPanelOpen]);
    var onTransitionEndEvent = function (e) {
        if (e.propertyName !== "transform")
            return;
        if (isGraphPanelOpen)
            return;
        setShouldRenderGraphData(false);
    };
    useEffect(function () {
        if (!isGraphPanelOpen)
            return;
        setShouldRenderGraphData(true);
    }, [isGraphPanelOpen]);
    // Closed from the popout, the docked panel comes back already closed, so
    // there is no slide-out transition to unmount the graph data after.
    useEffect(function () {
        if (!isGraphPanelOpen && graphPanelWindow)
            setShouldRenderGraphData(false);
    }, [isGraphPanelOpen, graphPanelWindow]);
    if (graphPanelWindow) {
        return _jsx(PopoutGraphPanel, { targetWindow: graphPanelWindow });
    }
    return (_jsxs(Styled.GraphPanel, { isOpen: isGraphPanelOpen, printMode: printModeActive, onTransitionEnd: onTransitionEndEvent, onPointerDown: isAutoOpened ? function () { return dispatch(actions.keepGraphPanelOpen()); } : undefined, children: [shouldRenderGraphData && !dashboardWindow && _jsx(CanvasBlocker, {}), _jsx(GraphPanelContent, { shouldRenderGraphData: shouldRenderGraphData })] }));
};
var GraphDataContainer = function () {
    var t = useTranslation().t;
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var graphPanelRef = useGraphDataContext().graphPanelRef;
    var xAxisDomain = useTimeDataMinMaxDomain();
    var _a = useGraphDataContext(), zoomedTimeDomain = _a.zoomedTimeDomain, airConsumptionGraphRef = _a.airConsumptionGraphRef;
    var chartElementPositionsData = useChartElementPositions();
    // The stored play position belongs to the application-speed timeline, where
    // before-pick phases happen before graph time starts at Pick.
    var zoomAdjustedPlayPercent = getGraphPlayLinePercent({
        playLocationPercent: playLocationPercent,
        playLocationTime: chartElementPositionsData
            ? chartElementPositionsData.percentToDisplayTime(playLocationPercent)
            : undefined,
        xAxisDomain: xAxisDomain,
        zoomedTimeDomain: zoomedTimeDomain,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var airGraphRef = airConsumptionGraphRef === null || airConsumptionGraphRef === void 0 ? void 0 : airConsumptionGraphRef.current;
    var drawingAreaLeft = airGraphRef === null || airGraphRef === void 0 ? void 0 : airGraphRef.state.offset.left;
    var drawingAreaWidth = airGraphRef === null || airGraphRef === void 0 ? void 0 : airGraphRef.state.offset.width;
    return (_jsx(Styled.GraphContainer, { ref: graphPanelRef, children: _jsxs(Styled.GraphContainerContent, { children: [_jsx(NodeList, {}), _jsxs(Styled.GraphSection, { children: [_jsx(ChartBackground, { children: _jsx(RobotMotionGraph, {}) }), _jsx(ChartBackground, { children: _jsx(AirConsumptionGraph, {}) }), _jsx(ChartBackground, { children: _jsx(VacuumPumpGraph, {}) }), _jsx(ChartBackground, { marginBottom: 60, children: _jsx(LiftingForcesGraph, {}) }), zoomAdjustedPlayPercent !== undefined && (_jsx(PlayLine, { playLocationPercent: zoomAdjustedPlayPercent })), zoomedTimeDomain &&
                            drawingAreaLeft !== undefined &&
                            drawingAreaWidth !== undefined && (_jsx(Styled.ZoomBorderOverlay, { left: drawingAreaLeft, width: drawingAreaWidth }))] }), _jsxs(Styled.CupAnimationContainer, { children: [_jsx(Styled.CupAnimationHeader, { children: t("graph_panel.cup-animation") }), _jsx(CupAnimation2D, {}), _jsx(GraphAnimationInfoBox, {})] })] }) }));
};
